import { formatTime } from '../../lib/chat-format';
import { MessageBubble as MetaBubble, type DeliveryStatus, type Seq } from './MetaBubbles';
import type { ChannelCapabilities, InboxMessage } from './types';

const STATUSES = new Set<DeliveryStatus>(['sent', 'delivered', 'read', 'failed']);
function toStatus(s: string | null | undefined): DeliveryStatus {
  return s && STATUSES.has(s as DeliveryStatus) ? (s as DeliveryStatus) : 'sent';
}

/**
 * One message bubble — renders each channel's native-Meta look via the shared
 * MetaBubbles shell (WhatsApp green + tail, Messenger blue, Instagram gradient).
 * The body comes from the channel's `toBubbleProps` mapper (typed per-type
 * layouts: text/media/buttons/quickReplies/cards/receipt/attachment) or, when a
 * channel hasn't supplied one yet, its `renderContent` JSX as the bubble body.
 * `seq` (from the thread's same-direction burst) groups corners + the WA tail.
 */
export function MessageBubble({
  m,
  capabilities,
  seq,
}: {
  m: InboxMessage;
  capabilities: ChannelCapabilities;
  seq: Seq;
}) {
  const body = capabilities.toBubbleProps?.(m) ?? {
    type: 'text' as const,
    children: capabilities.renderContent(m),
  };
  return (
    <MetaBubble
      channel={capabilities.channel}
      incoming={m.isIncoming}
      seq={seq}
      time={m.messagedAt ? formatTime(new Date(m.messagedAt)) : undefined}
      status={toStatus(m.status)}
      replyTo={m.replyTo?.preview ?? undefined}
      referral={m.referralHeadline ? { headline: m.referralHeadline } : undefined}
      {...body}
    />
  );
}
